import Input from "../general/Input.tsx";
import {
    BusinessRegistrationFormData,
    LEGAL_ENTITY_TYPE_OPTIONS,
} from "../../lib/businessRegistration.ts";
import type {LegalEntityType} from "../../generated/enums.ts";

interface BusinessRegistrationFormFieldsProps {
    formData: BusinessRegistrationFormData;
    errors: Record<string, string>;
    loading?: boolean;
    onFieldChange: <K extends keyof BusinessRegistrationFormData>(
        field: K,
        value: BusinessRegistrationFormData[K],
    ) => void;
    className?: string;
}

export default function BusinessRegistrationFormFields({
    formData,
    errors,
    loading = false,
    onFieldChange,
    className = "space-y-6",
}: BusinessRegistrationFormFieldsProps) {
    return (
        <div className={className}>
            <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-900">
                    Bedrijfsnaam
                </label>
                <div className="mt-2">
                    <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={(event) => onFieldChange("name", event.target.value)}
                        autoComplete="organization"
                        disabled={loading}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>
            </div>

            <div>
                <label htmlFor="legal_entity_type" className="block text-sm font-medium text-gray-900">
                    Rechtsvorm
                </label>
                <select
                    id="legal_entity_type"
                    name="legal_entity_type"
                    value={formData.legal_entity_type}
                    onChange={(event) => onFieldChange("legal_entity_type", event.target.value as LegalEntityType)}
                    disabled={loading}
                    className="mt-2 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 shadow-sm focus:border-primary focus:ring-primary"
                >
                    {LEGAL_ENTITY_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {errors.legal_entity_type && (
                    <p className="mt-1 text-sm text-red-600">{errors.legal_entity_type}</p>
                )}
            </div>

            <div>
                <label htmlFor="registration_number" className="block text-sm font-medium text-gray-900">
                    KVK-nummer
                </label>
                <div className="mt-2">
                    <Input
                        id="registration_number"
                        name="registration_number"
                        inputMode="numeric"
                        pattern="\\d{8}"
                        value={formData.registration_number}
                        onChange={(event) => onFieldChange("registration_number", event.target.value)}
                        disabled={loading}
                    />
                    {errors.registration_number && (
                        <p className="mt-1 text-sm text-red-600">{errors.registration_number}</p>
                    )}
                </div>
            </div>

            <div>
                <label htmlFor="fte_amount" className="block text-sm font-medium text-gray-900">
                    Aantal FTE&apos;s
                </label>
                <div className="mt-2">
                    <Input
                        id="fte_amount"
                        name="fte_amount"
                        type="number"
                        min={1}
                        value={formData.fte_amount}
                        onChange={(event) => onFieldChange("fte_amount", parseInt(event.target.value, 10) || 0)}
                        disabled={loading}
                    />
                    {errors.fte_amount && <p className="mt-1 text-sm text-red-600">{errors.fte_amount}</p>}
                </div>
            </div>

            <div>
                <label htmlFor="industry" className="block text-sm font-medium text-gray-900">
                    Branche
                </label>
                <div className="mt-2">
                    <Input
                        id="industry"
                        name="industry"
                        value={formData.industry}
                        onChange={(event) => onFieldChange("industry", event.target.value)}
                        autoComplete="off"
                        disabled={loading}
                    />
                    {errors.industry && <p className="mt-1 text-sm text-red-600">{errors.industry}</p>}
                </div>
            </div>

            <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-900">
                    Omschrijving
                </label>
                <div className="mt-2">
                    <textarea
                        id="description"
                        name="description"
                        rows={4}
                        value={formData.description}
                        onChange={(event) => onFieldChange("description", event.target.value)}
                        disabled={loading}
                        className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 shadow-sm focus:border-primary focus:ring-primary"
                    />
                    {errors.description && (
                        <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                    )}
                </div>
            </div>
        </div>
    );
}
